import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { rounds } from "../lib/api";
import { getGameColor, getGameName } from "../constants/theme";
import { toast } from "sonner";
import {
  Play, SkipForward, Trophy, Users, Clock, Gift, ChevronLeft,
  Printer, Download, UserMinus, Swords, Timer, Crown, Shuffle,
  Check, X, ExternalLink, Plus, Trash2, Award, Dices, ArrowUpDown,
} from "lucide-react";

const TABS = [
  { id: "pairings", label: "Pairings", icon: Swords },
  { id: "standings", label: "Standings", icon: ArrowUpDown },
  { id: "timer", label: "Timer", icon: Clock },
  { id: "prizes", label: "Lots", icon: Gift },
];

export default function TournamentManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pairings");
  const [state, setState] = useState(null);
  const [allRounds, setAllRounds] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [standings, setStandings] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [stateRes, roundsRes, standingsRes, prizesRes] = await Promise.all([
        rounds.getState(id),
        rounds.getRounds(id),
        rounds.getStandings(id).catch(() => ({ data: [] })),
        rounds.getPrizes(id).catch(() => ({ data: [] })),
      ]);
      setState(stateRes.data);
      setAllRounds(roundsRes.data);
      setStandings(standingsRes.data);
      setPrizes(prizesRes.data);
      if (roundsRes.data.length > 0) {
        const last = roundsRes.data[roundsRes.data.length - 1];
        setCurrentRound(last);
        if (selectedRound === null) setSelectedRound(last.round_number);
      }
    } catch (e) {
      toast.error("Erreur chargement données");
    } finally {
      setLoading(false);
    }
  }, [id, selectedRound]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await rounds.start(id, { round_time_minutes: 50, top_cut_size: 8 });
      toast.success("Tournoi démarré !");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setActionLoading(false); }
  };

  const handleNextRound = async () => {
    setActionLoading(true);
    try {
      const res = await rounds.nextRound(id);
      toast.success(`Ronde ${res.data.round_number} créée`);
      setSelectedRound(res.data.round_number);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setActionLoading(false); }
  };

  const handleTopCut = async () => {
    setActionLoading(true);
    try {
      await rounds.startTopCut(id, 8);
      toast.success("Top 8 lancé !");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setActionLoading(false); }
  };

  const handleComplete = async () => {
    if (!window.confirm("Terminer définitivement ce tournoi ?")) return;
    setActionLoading(true);
    try {
      await rounds.complete(id);
      toast.success("Tournoi terminé !");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setActionLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>;

  const phase = state?.phase || "not_started";
  const isStarted = state?.status === "in_progress";
  const gameColor = getGameColor(state?.game);
  const displayedRound = allRounds.find(r => r.round_number === selectedRound) || currentRound;

  return (
    <div className="max-w-7xl mx-auto" data-testid="tournament-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-[#252525] text-[#B0B0B0]" data-testid="back-button">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{state?.name || "Tournoi"}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: gameColor + "30", color: gameColor }}>
                {getGameName(state?.game)}
              </span>
              <span className="text-[#666] text-sm">{state?.format}</span>
              <span className="text-[#666] text-sm">|</span>
              <Users className="w-3 h-3 text-[#666]" />
              <span className="text-[#666] text-sm">{state?.participants_count} joueurs</span>
              {phase !== "not_started" && (
                <>
                  <span className="text-[#666] text-sm">|</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    phase === "swiss" ? "bg-blue-500/20 text-blue-400" :
                    phase === "top_cut" ? "bg-purple-500/20 text-purple-400" :
                    phase === "completed" ? "bg-green-500/20 text-green-400" : "bg-[#333] text-[#999]"
                  }`}>
                    {phase === "swiss" ? `Suisse R${state?.current_round}/${state?.total_swiss_rounds}` :
                     phase === "top_cut" ? `Top ${state?.top_cut_size}` :
                     phase === "completed" ? "Terminé" : "Non démarré"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isStarted && phase !== "completed" && (
            <button onClick={handleStart} disabled={actionLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:opacity-50" data-testid="start-tournament-btn">
              <Play className="w-4 h-4" /> Démarrer
            </button>
          )}
          {isStarted && phase === "swiss" && (
            <>
              <button onClick={handleNextRound} disabled={actionLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium transition-colors disabled:opacity-50" data-testid="next-round-btn">
                <SkipForward className="w-4 h-4" /> Ronde suivante
              </button>
              <button onClick={handleTopCut} disabled={actionLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors disabled:opacity-50" data-testid="top-cut-btn">
                <Crown className="w-4 h-4" /> Top Cut
              </button>
            </>
          )}
          {isStarted && phase === "top_cut" && (
            <button onClick={handleNextRound} disabled={actionLoading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors disabled:opacity-50" data-testid="next-topcut-btn">
              <SkipForward className="w-4 h-4" /> Match suivant
            </button>
          )}
          {isStarted && (
            <button onClick={handleComplete} disabled={actionLoading} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#333] hover:bg-[#444] text-[#B0B0B0] font-medium transition-colors disabled:opacity-50" data-testid="complete-btn">
              <Trophy className="w-4 h-4" /> Terminer
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#121212] p-1 rounded-xl border border-[#333]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all flex-1 justify-center ${
              tab === t.id ? "bg-[#FF6B35] text-white shadow-lg" : "text-[#B0B0B0] hover:bg-[#252525] hover:text-white"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "pairings" && <PairingsTab tournamentId={id} allRounds={allRounds} displayedRound={displayedRound} selectedRound={selectedRound} setSelectedRound={setSelectedRound} state={state} loadData={loadData} />}
      {tab === "standings" && <StandingsTab standings={standings} state={state} tournamentId={id} loadData={loadData} />}
      {tab === "timer" && <TimerTab state={state} currentRound={currentRound} standings={standings} allRounds={allRounds} prizes={prizes} tournamentId={id} />}
      {tab === "prizes" && <PrizesTab tournamentId={id} prizes={prizes} loadData={loadData} state={state} />}
    </div>
  );
}

/* ==================== PAIRINGS TAB ==================== */
function PairingsTab({ tournamentId, allRounds, displayedRound, selectedRound, setSelectedRound, state, loadData }) {
  const [editingPairing, setEditingPairing] = useState(null);
  const [resultForm, setResultForm] = useState({ p1_wins: 0, p2_wins: 0, draws: 0 });

  const handleSubmitResult = async () => {
    if (!editingPairing || !displayedRound) return;
    try {
      await rounds.submitResult(tournamentId, displayedRound.round_number, {
        pairing_id: editingPairing.id,
        ...resultForm,
      });
      toast.success("Résultat enregistré");
      setEditingPairing(null);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (!displayedRound && allRounds.length === 0) {
    return (
      <div className="text-center py-16 text-[#666]" data-testid="no-rounds">
        <Swords className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-lg">Aucune ronde créée</p>
        <p className="text-sm mt-1">Démarrez le tournoi puis lancez la première ronde</p>
      </div>
    );
  }

  return (
    <div>
      {/* Round selector */}
      {allRounds.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {allRounds.map(r => (
            <button key={`${r.round_type}-${r.round_number}`} onClick={() => setSelectedRound(r.round_number)} data-testid={`select-${r.round_type === "top_cut" ? "topcut" : "swiss"}-round-${r.round_number}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedRound === r.round_number ? "bg-[#FF6B35] text-white" :
                r.round_type === "top_cut" ? "bg-purple-500/10 text-purple-400 hover:bg-purple-500/20" :
                "bg-[#1a1a1a] text-[#B0B0B0] hover:bg-[#252525]"
              }`}>
              {r.round_type === "top_cut" ? `Top${r.round_number - (allRounds.filter(x => x.round_type === "swiss").length)}` : `R${r.round_number}`}
              {r.status === "completed" && <Check className="w-3 h-3 inline ml-1" />}
            </button>
          ))}
        </div>
      )}

      {displayedRound && (
        <div className="space-y-2" data-testid="pairings-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-white">
              {displayedRound.round_type === "top_cut" ? "Top Cut" : "Ronde"} {displayedRound.round_number}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${displayedRound.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                {displayedRound.status === "completed" ? "Terminée" : "En cours"}
              </span>
            </h3>
            <button onClick={() => printPairings(displayedRound)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm" data-testid="print-pairings-btn">
              <Printer className="w-3 h-3" /> Imprimer
            </button>
          </div>

          {displayedRound.pairings.map((p, i) => (
            <div key={p.id} className={`bg-[#1a1a1a] rounded-xl border ${p.result ? "border-[#333]" : "border-[#FF6B35]/30"} p-4`} data-testid={`pairing-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <span className="text-[#666] text-sm font-mono w-12">T{p.table}</span>
                  <div className="flex items-center gap-3 flex-1">
                    <span className={`font-medium ${p.winner_id === p.player1_id ? "text-green-400" : "text-white"}`}>
                      {p.player1_name}
                    </span>
                    {p.result ? (
                      <span className="text-[#FF6B35] font-bold text-sm px-2 py-0.5 bg-[#FF6B35]/10 rounded">
                        {p.result.p1_wins} - {p.result.p2_wins}{p.result.draws > 0 ? ` (${p.result.draws}N)` : ""}
                      </span>
                    ) : (
                      <span className="text-[#666] text-sm">vs</span>
                    )}
                    <span className={`font-medium ${p.winner_id === p.player2_id ? "text-green-400" : p.player2_id ? "text-white" : "text-[#666] italic"}`}>
                      {p.player2_name}
                    </span>
                  </div>
                </div>
                {!p.result && p.player2_id && displayedRound.status !== "completed" && (
                  <button onClick={() => { setEditingPairing(p); setResultForm({ p1_wins: 0, p2_wins: 0, draws: 0 }); }}
                    className="px-3 py-1.5 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white text-sm font-medium" data-testid={`enter-result-${i}`}>
                    Résultat
                  </button>
                )}
                {p.result && !p.player2_id && (
                  <span className="text-[#666] text-xs italic">BYE</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Result modal */}
      {editingPairing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="result-modal">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-4">Saisir le résultat</h3>
            <div className="flex items-center justify-between mb-6">
              <div className="text-center flex-1">
                <p className="text-white font-medium">{editingPairing.player1_name}</p>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <button onClick={() => setResultForm(f => ({ ...f, p1_wins: Math.max(0, f.p1_wins - 1) }))}
                    className="w-8 h-8 rounded-full bg-[#333] hover:bg-[#444] text-white flex items-center justify-center">-</button>
                  <span className="text-3xl font-bold text-white w-8 text-center">{resultForm.p1_wins}</span>
                  <button onClick={() => setResultForm(f => ({ ...f, p1_wins: f.p1_wins + 1 }))}
                    className="w-8 h-8 rounded-full bg-[#FF6B35] hover:bg-[#E55A25] text-white flex items-center justify-center">+</button>
                </div>
              </div>
              <div className="text-[#666] font-bold text-lg mx-4">VS</div>
              <div className="text-center flex-1">
                <p className="text-white font-medium">{editingPairing.player2_name}</p>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <button onClick={() => setResultForm(f => ({ ...f, p2_wins: Math.max(0, f.p2_wins - 1) }))}
                    className="w-8 h-8 rounded-full bg-[#333] hover:bg-[#444] text-white flex items-center justify-center">-</button>
                  <span className="text-3xl font-bold text-white w-8 text-center">{resultForm.p2_wins}</span>
                  <button onClick={() => setResultForm(f => ({ ...f, p2_wins: f.p2_wins + 1 }))}
                    className="w-8 h-8 rounded-full bg-[#FF6B35] hover:bg-[#E55A25] text-white flex items-center justify-center">+</button>
                </div>
              </div>
            </div>
            <div className="text-center mb-6">
              <label className="text-[#666] text-sm">Nulles</label>
              <div className="flex items-center justify-center gap-3 mt-1">
                <button onClick={() => setResultForm(f => ({ ...f, draws: Math.max(0, f.draws - 1) }))}
                  className="w-8 h-8 rounded-full bg-[#333] hover:bg-[#444] text-white flex items-center justify-center">-</button>
                <span className="text-xl font-bold text-[#B0B0B0] w-8 text-center">{resultForm.draws}</span>
                <button onClick={() => setResultForm(f => ({ ...f, draws: f.draws + 1 }))}
                  className="w-8 h-8 rounded-full bg-[#333] hover:bg-[#444] text-white flex items-center justify-center">+</button>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingPairing(null)} className="flex-1 py-2.5 rounded-xl bg-[#333] hover:bg-[#444] text-[#B0B0B0] font-medium" data-testid="cancel-result-btn">Annuler</button>
              <button onClick={handleSubmitResult} className="flex-1 py-2.5 rounded-xl bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium" data-testid="confirm-result-btn">Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function printPairings(round) {
  const sorted = [...round.pairings].sort((a, b) => a.player1_name.localeCompare(b.player1_name));
  const html = `<html><head><title>Pairings R${round.round_number}</title><style>
    body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;margin-top:10px}
    th,td{border:1px solid #333;padding:8px;text-align:left}th{background:#f5f5f5;font-weight:bold}
    h1{font-size:18px}@media print{body{padding:0}}</style></head><body>
    <h1>Ronde ${round.round_number} - Pairings</h1>
    <table><tr><th>Table</th><th>Joueur 1</th><th>Points</th><th>Joueur 2</th><th>Points</th></tr>
    ${sorted.map(p => `<tr><td>${p.table}</td><td>${p.player1_name}</td><td></td><td>${p.player2_name}</td><td></td></tr>`).join("")}
    </table></body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.print();
}


/* ==================== STANDINGS TAB ==================== */
function StandingsTab({ standings, state, tournamentId, loadData }) {
  const [dropping, setDropping] = useState(null);

  const handleDrop = async (playerId, playerName) => {
    if (!window.confirm(`Retirer ${playerName} du tournoi ?`)) return;
    setDropping(playerId);
    try {
      await rounds.dropPlayer(tournamentId, playerId);
      toast.success(`${playerName} retiré`);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setDropping(null); }
  };

  const handlePrint = () => {
    const html = `<html><head><title>Standings</title><style>
      body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse;margin-top:10px}
      th,td{border:1px solid #333;padding:8px;text-align:left}th{background:#f5f5f5;font-weight:bold}
      h1{font-size:18px}.dropped{color:#999;text-decoration:line-through}@media print{body{padding:0}}</style></head><body>
      <h1>${state?.name || "Tournoi"} - Standings</h1>
      <table><tr><th>#</th><th>Joueur</th><th>Pts</th><th>V-D-N</th><th>OMW%</th><th>GW%</th><th>OGW%</th></tr>
      ${standings.map(s => `<tr class="${s.dropped ? 'dropped' : ''}"><td>${s.rank}</td><td>${s.player_name}</td><td>${s.match_points}</td>
        <td>${s.match_wins}-${s.match_losses}-${s.match_draws}</td><td>${s.omwp}%</td><td>${s.gwp}%</td><td>${s.ogwp}%</td></tr>`).join("")}
      </table></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.print();
  };

  const downloadCSV = () => {
    const header = "Rang,Joueur,Points,Victoires,Defaites,Nulles,OMW%,GW%,OGW%,Drop\n";
    const rows = standings.map(s =>
      `${s.rank},"${s.player_name}",${s.match_points},${s.match_wins},${s.match_losses},${s.match_draws},${s.omwp},${s.gwp},${s.ogwp},${s.dropped ? "Oui" : "Non"}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `standings-${tournamentId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">Classement</h3>
        <div className="flex gap-2">
          <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm" data-testid="print-standings-btn">
            <Printer className="w-3 h-3" /> Imprimer
          </button>
          <button onClick={downloadCSV} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm" data-testid="download-csv-btn">
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      </div>

      {standings.length === 0 ? (
        <div className="text-center py-16 text-[#666]" data-testid="no-standings">
          <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Aucun classement disponible</p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="standings-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#333]">
                <th className="text-left text-xs font-bold text-[#666] uppercase py-3 px-3">#</th>
                <th className="text-left text-xs font-bold text-[#666] uppercase py-3 px-3">Joueur</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3">Pts</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3">V-D-N</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3">OMW%</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3">GW%</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3">OGW%</th>
                <th className="text-center text-xs font-bold text-[#666] uppercase py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={s.player_id} className={`border-b border-[#222] hover:bg-[#1a1a1a] transition-colors ${s.dropped ? "opacity-40" : ""}`} data-testid={`standing-row-${i}`}>
                  <td className="py-3 px-3">
                    <span className={`font-bold ${i === 0 ? "text-yellow-400" : i === 1 ? "text-gray-300" : i === 2 ? "text-orange-400" : "text-[#666]"}`}>
                      {s.rank}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`font-medium ${s.dropped ? "line-through text-[#666]" : "text-white"}`}>{s.player_name}</span>
                    {s.dropped && <span className="ml-2 text-xs text-red-400">DROP</span>}
                  </td>
                  <td className="py-3 px-3 text-center"><span className="font-bold text-[#FF6B35]">{s.match_points}</span></td>
                  <td className="py-3 px-3 text-center text-[#B0B0B0] text-sm">{s.match_wins}-{s.match_losses}-{s.match_draws}</td>
                  <td className="py-3 px-3 text-center text-[#B0B0B0] text-sm">{s.omwp}%</td>
                  <td className="py-3 px-3 text-center text-[#B0B0B0] text-sm">{s.gwp}%</td>
                  <td className="py-3 px-3 text-center text-[#B0B0B0] text-sm">{s.ogwp}%</td>
                  <td className="py-3 px-3 text-center">
                    {!s.dropped && state?.status === "in_progress" && (
                      <button onClick={() => handleDrop(s.player_id, s.player_name)} disabled={dropping === s.player_id}
                        className="p-1.5 rounded-lg hover:bg-red-500/20 text-[#666] hover:text-red-400 transition-colors" data-testid={`drop-player-${i}`}>
                        <UserMinus className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ==================== TIMER TAB ==================== */
function TimerTab({ state, currentRound, standings, allRounds, prizes, tournamentId }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState((state?.round_time_minutes || 50) * 60);
  const timerRef = useRef(null);
  const channelRef = useRef(null);

  // BroadcastChannel for real-time sync with external window
  useEffect(() => {
    channelRef.current = new BroadcastChannel(`arcanapp-timer-${tournamentId}`);
    return () => channelRef.current?.close();
  }, [tournamentId]);

  const broadcast = useCallback((data) => {
    try { channelRef.current?.postMessage(data); } catch {}
  }, []);

  useEffect(() => {
    if (timeLeft === null) setTimeLeft(totalSeconds);
  }, [totalSeconds, timeLeft]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          const next = t - 1;
          if (next <= 0) {
            clearInterval(timerRef.current);
            setIsRunning(false);
            broadcast({ type: "tick", timeLeft: 0, isRunning: false, total: totalSeconds });
            return 0;
          }
          broadcast({ type: "tick", timeLeft: next, isRunning: true, total: totalSeconds });
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning, timeLeft, totalSeconds, broadcast]);

  const toggleTimer = () => {
    const next = !isRunning;
    setIsRunning(next);
    broadcast({ type: "control", action: next ? "start" : "pause", timeLeft: timeLeft || 0, total: totalSeconds });
  };
  const resetTimer = () => {
    setIsRunning(false);
    clearInterval(timerRef.current);
    setTimeLeft(totalSeconds);
    broadcast({ type: "control", action: "reset", timeLeft: totalSeconds, total: totalSeconds });
  };
  const addMinutes = (m) => {
    setTimeLeft(t => {
      const next = Math.max(0, t + m * 60);
      broadcast({ type: "control", action: "set", timeLeft: next, total: totalSeconds });
      return next;
    });
  };

  const mins = Math.floor((timeLeft || 0) / 60);
  const secs = (timeLeft || 0) % 60;
  const progress = totalSeconds > 0 ? ((totalSeconds - (timeLeft || 0)) / totalSeconds) * 100 : 0;

  const openExternalTimer = () => {
    const w = window.open("", "ArcanappTimer", "width=1200,height=800");
    if (!w) { toast.error("Popup bloqué"); return; }
    const roundInfo = currentRound ? `Ronde ${currentRound.round_number}` : "";
    const pairingsHtml = currentRound ? currentRound.pairings.map(p =>
      `<tr><td class="t">${p.table}</td><td>${p.player1_name}</td><td class="vs">vs</td><td>${p.player2_name}</td>
       <td class="r">${p.result ? `${p.result.p1_wins}-${p.result.p2_wins}` : "-"}</td></tr>`
    ).join("") : "";
    const standingsHtml = standings.slice(0, 16).map((s) =>
      `<tr><td class="rk">${s.rank}</td><td>${s.player_name}</td><td class="pts">${s.match_points}</td>
       <td>${s.match_wins}-${s.match_losses}-${s.match_draws}</td><td>${s.omwp}%</td></tr>`
    ).join("");
    const prizesHtml = prizes.map(p =>
      `<div class="prize ${p.winner_id ? 'won' : ''}">${p.name}${p.winner_id ? ` - ${p.winner_name}` : ""}</div>`
    ).join("");

    w.document.write(`<html><head><title>Timer - ${state?.name || "Tournoi"}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{background:#0a0a0a;color:#fff;font-family:'Segoe UI',Arial,sans-serif;overflow:hidden}
      .header{background:#121212;padding:16px 24px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #333}
      .header h1{font-size:20px;color:#FF6B35} .header .round{color:#B0B0B0;font-size:14px}
      .main{display:grid;grid-template-columns:1fr 1fr;height:calc(100vh - 60px)}
      .timer-section{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:40px;border-right:1px solid #333}
      .timer-display{font-size:120px;font-weight:900;font-variant-numeric:tabular-nums;letter-spacing:-2px;transition:color 0.3s}
      .timer-display.warning{color:#FFC107}.timer-display.danger{color:#FF5252;animation:pulse 1s infinite}.timer-display.normal{color:#4ECDC4}
      @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
      .progress{width:80%;height:6px;background:#333;border-radius:3px;margin-top:20px;overflow:hidden}
      .progress-bar{height:100%;background:#FF6B35;border-radius:3px;transition:width 1s linear}
      .alert-msg{display:none;margin-top:30px;padding:20px;background:#FF5252;border-radius:16px;font-size:18px;font-weight:700;text-align:center;line-height:1.5;animation:fadeIn 0.5s}
      .alert-msg.show{display:block}
      @keyframes fadeIn{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}
      .right{overflow-y:auto;padding:20px}
      .section-title{font-size:14px;color:#666;text-transform:uppercase;letter-spacing:1px;margin:16px 0 8px;font-weight:700}
      table{width:100%;border-collapse:collapse} td,th{padding:6px 8px;text-align:left;font-size:13px;border-bottom:1px solid #222}
      .t{color:#666;font-family:monospace}.vs{color:#666;text-align:center}.r{color:#FF6B35;font-weight:bold;text-align:right}
      .rk{color:#FF6B35;font-weight:bold}.pts{color:#4ECDC4;font-weight:bold}
      .prize{padding:8px 12px;background:#1a1a1a;border-radius:8px;margin:4px 0;font-size:13px;border:1px solid #333}
      .prize.won{border-color:#4CAF50;color:#4CAF50}
    </style></head><body>
    <div class="header"><h1>${state?.name || "Tournoi"}</h1><div class="round">${roundInfo}</div></div>
    <div class="main">
      <div class="timer-section">
        <div class="timer-display normal" id="timer">${String(mins).padStart(2,"0")}:${String(secs).padStart(2,"0")}</div>
        <div class="progress"><div class="progress-bar" id="prog" style="width:${progress}%"></div></div>
        <div class="alert-msg" id="alert">Fin du temps !<br/>Le joueur actif finit son tour.<br/>5 tours additionnels à partager.</div>
      </div>
      <div class="right">
        <div class="section-title">Pairings</div>
        <table>${pairingsHtml}</table>
        <div class="section-title">Standings</div>
        <table>${standingsHtml}</table>
        ${prizesHtml ? `<div class="section-title">Lots</div>${prizesHtml}` : ""}
      </div>
    </div>
    <script>
      let t=${timeLeft || 0},total=${totalSeconds},alerted=false;
      const ch=new BroadcastChannel('arcanapp-timer-${tournamentId}');
      function update(){
        const m=Math.floor(t/60),s=t%60;
        const el=document.getElementById("timer");
        el.textContent=String(m).padStart(2,"0")+":"+String(s).padStart(2,"0");
        el.className="timer-display "+(t<=0?"danger":t<60?"danger":t<300?"warning":"normal");
        document.getElementById("prog").style.width=((total-t)/total*100)+"%";
        if(t<=0&&!alerted){
          alerted=true;
          document.getElementById("alert").classList.add("show");
          try{
            const ctx=new(window.AudioContext||window.webkitAudioContext)();
            const osc=ctx.createOscillator();const gain=ctx.createGain();
            osc.connect(gain);gain.connect(ctx.destination);
            osc.frequency.value=880;osc.type="square";gain.gain.value=0.3;
            osc.start();setTimeout(()=>{osc.stop();ctx.close()},1500);
          }catch(e){}
        }
      }
      ch.onmessage=function(e){
        const d=e.data;
        if(d.type==="tick"){t=d.timeLeft;total=d.total||total;update();}
        if(d.type==="control"){
          t=d.timeLeft;total=d.total||total;
          if(d.action==="reset"){alerted=false;document.getElementById("alert").classList.remove("show");}
          update();
        }
      };
      update();
    </script></body></html>`);
    w.document.close();
    // Send initial state
    broadcast({ type: "control", action: "sync", timeLeft: timeLeft || 0, total: totalSeconds });
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      {/* Timer display */}
      <div className="bg-[#121212] rounded-2xl border border-[#333] p-8 mb-6" data-testid="timer-display">
        <div className={`text-8xl font-black tabular-nums tracking-tight ${
          timeLeft === 0 ? "text-red-500 animate-pulse" : timeLeft < 60 ? "text-red-400" : timeLeft < 300 ? "text-yellow-400" : "text-[#4ECDC4]"
        }`}>
          {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </div>
        <div className="w-full h-1.5 bg-[#333] rounded-full mt-6 overflow-hidden">
          <div className="h-full bg-[#FF6B35] rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <button onClick={() => addMinutes(-5)} className="px-3 py-2 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm">-5min</button>
        <button onClick={() => addMinutes(-1)} className="px-3 py-2 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm">-1min</button>
        <button onClick={toggleTimer} data-testid="timer-toggle-btn"
          className={`px-8 py-3 rounded-xl font-bold text-lg transition-colors ${
            isRunning ? "bg-red-600 hover:bg-red-700 text-white" : "bg-green-600 hover:bg-green-700 text-white"
          }`}>
          {isRunning ? "Pause" : "Démarrer"}
        </button>
        <button onClick={() => addMinutes(1)} className="px-3 py-2 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm">+1min</button>
        <button onClick={() => addMinutes(5)} className="px-3 py-2 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm">+5min</button>
      </div>

      <div className="flex items-center justify-center gap-3 mb-8">
        <button onClick={resetTimer} className="px-4 py-2 rounded-lg bg-[#333] hover:bg-[#444] text-[#B0B0B0] text-sm" data-testid="timer-reset-btn">
          Reset ({state?.round_time_minutes || 50}min)
        </button>
        <button onClick={openExternalTimer} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] text-[#B0B0B0] text-sm border border-[#333]" data-testid="external-timer-btn">
          <ExternalLink className="w-4 h-4" /> Fenêtre externe
        </button>
      </div>

      {/* Quick config */}
      <div className="bg-[#121212] rounded-xl border border-[#333] p-4">
        <label className="text-[#666] text-sm">Durée par ronde (minutes)</label>
        <input type="number" value={Math.floor(totalSeconds / 60)} onChange={e => { const v = parseInt(e.target.value) || 50; setTotalSeconds(v * 60); setTimeLeft(v * 60); setIsRunning(false); }}
          className="mt-2 w-24 px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#333] text-white text-center" data-testid="timer-minutes-input" />
      </div>
    </div>
  );
}


/* ==================== PRIZES TAB ==================== */
function PrizesTab({ tournamentId, prizes, loadData, state }) {
  const [newPrize, setNewPrize] = useState({ name: "", description: "", quantity: 1 });
  const [raffleResult, setRaffleResult] = useState(null);
  const [raffleAnimating, setRaffleAnimating] = useState(false);
  const [raffleNames, setRaffleNames] = useState([]);
  const animRef = useRef(null);

  const handleAddPrize = async () => {
    if (!newPrize.name.trim()) return;
    try {
      await rounds.addPrize(tournamentId, newPrize);
      setNewPrize({ name: "", description: "", quantity: 1 });
      toast.success("Lot ajouté");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleRemovePrize = async (prizeId) => {
    try {
      await rounds.removePrize(tournamentId, prizeId);
      toast.success("Lot supprimé");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleRaffle = async () => {
    setRaffleResult(null);
    setRaffleAnimating(true);
    try {
      const res = await rounds.raffle(tournamentId);
      const data = res.data;
      const names = data.eligible_players.map(p => p.name);
      // Animate through names for 4 seconds
      let idx = 0;
      setRaffleNames(names);
      animRef.current = setInterval(() => {
        idx = (idx + 1) % names.length;
        setRaffleNames(prev => {
          const copy = [...prev];
          copy.unshift(copy.pop());
          return copy;
        });
      }, 100);

      setTimeout(() => {
        clearInterval(animRef.current);
        setRaffleAnimating(false);
        setRaffleResult(data);
        loadData();
      }, 4000);
    } catch (e) {
      setRaffleAnimating(false);
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Add prize */}
      <div className="bg-[#121212] rounded-xl border border-[#333] p-4 mb-6">
        <h3 className="text-sm font-bold text-[#666] uppercase mb-3">Ajouter un lot</h3>
        <div className="flex gap-3">
          <input type="text" placeholder="Nom du lot" value={newPrize.name} onChange={e => setNewPrize(p => ({ ...p, name: e.target.value }))}
            className="flex-1 px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#333] text-white placeholder-[#666]" data-testid="prize-name-input" />
          <input type="text" placeholder="Description (opt.)" value={newPrize.description} onChange={e => setNewPrize(p => ({ ...p, description: e.target.value }))}
            className="flex-1 px-3 py-2 rounded-lg bg-[#0a0a0a] border border-[#333] text-white placeholder-[#666]" data-testid="prize-desc-input" />
          <button onClick={handleAddPrize} className="px-4 py-2 rounded-lg bg-[#FF6B35] hover:bg-[#E55A25] text-white font-medium" data-testid="add-prize-btn">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Prize list */}
      <div className="space-y-2 mb-6" data-testid="prizes-list">
        {prizes.length === 0 ? (
          <div className="text-center py-12 text-[#666]">
            <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun lot ajouté</p>
          </div>
        ) : prizes.map((p, i) => (
          <div key={p.id} className={`flex items-center justify-between p-4 rounded-xl border ${
            p.winner_id ? "bg-green-500/5 border-green-500/30" : "bg-[#1a1a1a] border-[#333]"
          }`} data-testid={`prize-${i}`}>
            <div>
              <span className="text-white font-medium">{p.name}</span>
              {p.description && <span className="text-[#666] text-sm ml-2">{p.description}</span>}
              {p.winner_name && (
                <span className="ml-3 text-green-400 text-sm font-medium">
                  <Award className="w-3 h-3 inline mr-1" />{p.winner_name}
                </span>
              )}
            </div>
            {!p.winner_id && (
              <button onClick={() => handleRemovePrize(p.id)} className="p-2 rounded-lg hover:bg-red-500/20 text-[#666] hover:text-red-400" data-testid={`remove-prize-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Raffle button */}
      {prizes.some(p => !p.winner_id) && (
        <div className="text-center">
          <button onClick={handleRaffle} disabled={raffleAnimating} data-testid="raffle-btn"
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#E55A25] hover:from-[#E55A25] hover:to-[#D04A15] text-white font-bold text-lg shadow-lg shadow-[#FF6B35]/20 disabled:opacity-50 transition-all">
            <Dices className="w-5 h-5 inline mr-2" /> Tirage au sort
          </button>
        </div>
      )}

      {/* Raffle animation */}
      {raffleAnimating && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="raffle-animation">
          <div className="text-center">
            <Dices className="w-16 h-16 text-[#FF6B35] mx-auto mb-4 animate-spin" />
            <div className="text-4xl font-black text-white animate-pulse">
              {raffleNames[0] || "..."}
            </div>
          </div>
        </div>
      )}

      {/* Raffle result */}
      {raffleResult && !raffleAnimating && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="raffle-result" onClick={() => setRaffleResult(null)}>
          <div className="text-center bg-[#1a1a1a] border border-[#FF6B35] rounded-3xl p-10 max-w-md shadow-2xl shadow-[#FF6B35]/20">
            <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <p className="text-[#666] text-sm uppercase tracking-wider mb-2">Gagnant du lot</p>
            <p className="text-[#FF6B35] text-lg font-medium mb-3">{raffleResult.prize}</p>
            <p className="text-4xl font-black text-white">{raffleResult.winner_name}</p>
            <p className="text-[#666] text-sm mt-6">Cliquez pour fermer</p>
          </div>
        </div>
      )}
    </div>
  );
}
